import React, { ReactNode } from 'react'
import classnames from 'classnames/bind'
import type {
  WorkflowResource,
  ShallowTaskWorkflowMilestone,
} from '@adc/sdk/types'
import Text from '../elementals/text'
import { formatMoney } from '../../utils/formatters'
// @ts-ignore
import styles from './styles.scss'

const cx = classnames.bind(styles)

/**
 * Maps a supporting-data `value_type` to a display formatter. Values whose
 * `value_type` has no entry here (including the default `"text"`) render
 * verbatim. Add an entry to support a new formatter — no component changes needed.
 */
const VALUE_FORMATTERS: Record<string, (value: string) => string> = {
  currency: value =>
    formatMoney(Number(value), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) as string,
}

interface InstructionsBannerProps<
  T extends WorkflowResource = WorkflowResource
> {
  task: ShallowTaskWorkflowMilestone
  resource: T
  instructionActionSlot?:
    | ((args: {
        task: ShallowTaskWorkflowMilestone
        resource: T
        action: string
      }) => ReactNode)
    | null
}

/**
 * Renders an instructions banner below a workflow milestone.
 * Shows header, supporting data bullets, body text, and an optional action button.
 * The action button delegates to the consumer via the instructionAction slot.
 */
function InstructionsBanner<T extends WorkflowResource = WorkflowResource>({
  task,
  resource,
  instructionActionSlot,
}: InstructionsBannerProps<T>) {
  const instructions = task.instructions
  if (!instructions) return null

  const { header, body, supporting_data, action } = instructions

  return (
    <div
      className={cx('instructions-banner')}
      data-elm-id={`instructions_banner_${task.code}`.toLowerCase()}
    >
      <div className={cx('instructions-content')}>
        {header && (
          <Text
            size='small'
            weight='black'
            color='action'
            elmId={`instructions_header_${task.code}`.toLowerCase()}
            className={cx('instructions-header')}
          >
            {header}
          </Text>
        )}

        {supporting_data && supporting_data.length > 0 && (
          <ul
            className={cx('instructions-supporting-data')}
            data-elm-id={`instructions_supporting_data_${task.code}`.toLowerCase()}
          >
            {supporting_data.map((item, idx: number) => {
              const formatter = item.value_type
                ? VALUE_FORMATTERS[item.value_type]
                : undefined
              const displayValue =
                formatter && item.value != null
                  ? formatter(item.value)
                  : item.value
              return (
                <li
                  key={idx}
                  data-elm-id={`instructions_supporting_data_item_${task.code}_${idx}`.toLowerCase()}
                >
                  {item.key ? (
                    <Text size='tiny' color='action'>
                      <Text
                        as='span'
                        size='tiny'
                        color='action'
                        weight='black'
                        elmId={`instructions_supporting_data_key_${task.code}_${idx}`.toLowerCase()}
                      >
                        {item.key}:
                      </Text>{' '}
                      <Text
                        as='span'
                        size='tiny'
                        color='action'
                        weight='medium'
                        elmId={`instructions_supporting_data_value_${task.code}_${idx}`.toLowerCase()}
                      >
                        {displayValue}
                      </Text>
                    </Text>
                  ) : (
                    <Text
                      size='tiny'
                      color='action'
                      weight='medium'
                      elmId={`instructions_supporting_data_value_${task.code}_${idx}`.toLowerCase()}
                    >
                      {displayValue}
                    </Text>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {body && (
          <Text
            size='tiny'
            color='action'
            className={cx('instructions-body')}
            elmId={`instructions_body_${task.code}`.toLowerCase()}
          >
            {body}
          </Text>
        )}
      </div>

      {action && instructionActionSlot && (
        <div
          className={cx('instructions-action')}
          data-elm-id={`instructions_action_${task.code}`.toLowerCase()}
        >
          {instructionActionSlot({ task, resource, action })}
        </div>
      )}
    </div>
  )
}

export default InstructionsBanner
